/**
 * Whether a surface may identify and track this visitor — the one copy of that decision.
 *
 * ADR 0018: **no cookie and no events before consent.** A behavioral identifier is not strictly
 * necessary under ePrivacy / § 25 TDDDG, so storing `_cin_pulse` needs consent whatever the legal
 * basis for processing the events turns out to be. That makes consent a gate in front of identity,
 * not a flag beside it.
 *
 * Framework-free and dependency-free, like `cookie.ts` beside it, and for the same reason: a
 * Symfony or Python surface mirrors this rather than reimplementing it from prose (ADR 0014). The
 * whole module is a pure function over a cookie header, so it runs in Node, in an edge runtime and
 * in a test with no DOM.
 *
 * **Two implementations of one interface, deliberately.** Cineamo's own apps read their banner's
 * cookie; a partner reads their CMP. Both produce a {@link PulseConsent}, and the route handler
 * neither knows nor cares which it got — that is what stops a partner integration growing a second
 * consent mechanism with its own bugs.
 */

/**
 * The cookie Cineamo's own banner writes when a visitor accepts analysis cookies.
 *
 * JS-set, host-only, 180 days — see `COOKIE_CONSENT_LIFETIME_DAYS` in `@cineamo/ui`. It is
 * readable from `document.cookie` *and* sent with same-origin requests, which is what lets the
 * client gate cheaply and the server gate authoritatively off the same fact.
 */
export const PULSE_CONSENT_COOKIE_NAME = '__analysis_cookies_opt_in';

/**
 * The one event a withdrawn visitor is still allowed to send.
 *
 * It needs **no** `interest_event_type_config` row: the ingest applies the status, writes the
 * `interest_consent_log` transition and then rejects the event itself, so it never becomes a stored
 * event. Sending a type that has no config row would normally be dropped loudly as an unknown type;
 * this one never gets that far.
 */
export const CONSENT_WITHDRAWN_EVENT_TYPE = 'consent_withdrawn';

/**
 * The `window` event a cookie banner dispatches when preferences are saved.
 *
 * Declared here rather than imported from `@cineamo/ui` because this package ships standalone with
 * no dependencies — a partner embedding it has no Cineamo UI at all. Cineamo's own apps dispatch
 * this exact name; `apps/cinema-website` used to dispatch `cineamo-cookie-consent-change` instead,
 * which meant a listener written against either name was silently dead in one of the two apps
 * (fixed in CIN-14857).
 *
 * A surface with a different banner passes its own name rather than renaming this one.
 */
export const PULSE_CONSENT_CHANGED_EVENT = 'cineamo:cookie-consent-changed';

/**
 * Mirrors `CinData\Enum\Interest\ConsentStatus`. `unknown` is what "no decision yet" resolves to,
 * and the ingest treats it as not-granted — silence is not consent.
 */
export type PulseConsentStatus = 'granted' | 'denied' | 'withdrawn' | 'unknown';

/**
 * A consent decision plus the evidence for it, in the shape `/v1/track` accepts.
 *
 * `collectionPoint` and `type` are backend enums (`ConsentCollectionPoint`, `ConsentType`), not free
 * text: an unrecognized value is silently replaced with a default at the ingest, so inventing one
 * loses the evidence rather than recording it. Which *host* collected the consent is not in this
 * shape on purpose — it already travels on every event as `context.surface`.
 */
export type PulseConsent = {
    status: PulseConsentStatus;
    /** Backend `ConsentType`. `analytics` for an analysis-cookies banner toggle. */
    type: string;
    /** Backend `ConsentCollectionPoint`. `cookie_banner` for ours, `partner_cmp` for a CMP. */
    collectionPoint: string;
    /** A CMP's raw consent string, when there is one. Ours has none. */
    consentString?: string;
};

/** Cineamo's own banner, as evidence. */
const FIRST_PARTY_BANNER = {
    type: 'analytics',
    collectionPoint: 'cookie_banner'
} as const;

/**
 * Reads one cookie out of a `Cookie:` header or a `document.cookie` string.
 *
 * Deliberately not a regex over the whole header: a cookie whose *value* contains the name of
 * another would match, and `__analysis_cookies_opt_in` is a substring of nothing today but is one
 * typo away from being one. Splitting on `;` and comparing the name exactly cannot go wrong that
 * way.
 */
export function readCookie(cookieHeader: string | undefined | null, name: string): string | undefined {
    if (!cookieHeader) return undefined;

    for (const part of cookieHeader.split(';')) {
        const separator = part.indexOf('=');
        if (separator === -1) continue;

        if (part.slice(0, separator).trim() === name) {
            return part.slice(separator + 1).trim();
        }
    }

    return undefined;
}

/**
 * The first-party resolver: Cineamo's banner cookie in, a consent decision out.
 *
 * **Only a literal `true` is consent.** No cookie, an empty value, `false`, or anything else at all
 * resolves to `unknown` — a visitor who has not answered the banner has not agreed to anything, and
 * a gate that treats absence as permission is the failure § 25 TDDDG is about. `unknown` rather than
 * `denied` because the two are different facts and the consent log records which: `denied` means
 * they were asked and said no.
 *
 * `cookieName` is for a surface whose consent cookie is not ours — the same name it passes to
 * {@link syncPulseConsent}, the tracker and the withdrawal hook.
 */
export function resolveConsentFromCookieHeader(
    cookieHeader: string | undefined | null,
    cookieName: string = PULSE_CONSENT_COOKIE_NAME
): PulseConsent {
    const value = readCookie(cookieHeader, cookieName);

    if (value === 'true') {
        return { status: 'granted', ...FIRST_PARTY_BANNER };
    }

    return { status: value === 'false' ? 'denied' : 'unknown', ...FIRST_PARTY_BANNER };
}

/** The evidence to attach to an explicit withdrawal from the first-party banner. */
export function firstPartyWithdrawal(): PulseConsent {
    return { status: 'withdrawn', ...FIRST_PARTY_BANNER };
}

/**
 * Whether a decision permits identifying and tracking this visitor.
 *
 * One function so that every gate — the tracker, the route handler, the middleware — asks the same
 * question. Three of the four statuses are not consent, and only one of them is `denied`, which is
 * exactly the mistake a `!== 'denied'` check makes.
 */
export function isConsentGranted(consent: PulseConsent | undefined | null): boolean {
    return consent?.status === 'granted';
}

/** What a door that can write `_cin_pulse` should do with it. */
export type PulseIdentifierAction = 'mint' | 'keep' | 'clear';

/**
 * Whether this visitor should be given an identifier, keep the one they have, or have it taken back.
 *
 * ADR 0018 is a rule about **storing** a behavioral identifier, not only about minting one — § 25
 * TDDDG is about the storage. The cookie is written for a year and the banner's decision lasts 180
 * days, so "no identifier before consent" on its own leaves every withdrawn or lapsed visitor
 * carrying an id for months with nothing on record permitting it.
 *
 * One function so the two doors cannot disagree: the route handler, which mints on the first
 * tracked event, and {@link ensureAnonymousIdCookie}, which mints on a page response. Each of them
 * knows whether a cookie is already there and what the resolver said; neither should be deciding
 * what that means.
 *
 * A withdrawal is the one event that reaches a closed gate, so it is named rather than inferred: by
 * the time it is sent the consent cookie is already gone, and the identifier it was sent under is
 * exactly the one the visitor is asking us to stop using.
 *
 * Nothing stored is never `clear` — there is nothing to take back, and minting an id in order to
 * expire it would create the record the visitor is asking us not to keep.
 */
export function pulseIdentifierAction(input: {
    consent: PulseConsent;
    hasIdentifier: boolean;
    isWithdrawal?: boolean;
}): PulseIdentifierAction {
    if (!input.hasIdentifier) {
        return !input.isWithdrawal && isConsentGranted(input.consent) ? 'mint' : 'keep';
    }

    if (input.isWithdrawal) return 'clear';

    return isConsentGranted(input.consent) ? 'keep' : 'clear';
}

/** Matches `COOKIE_CONSENT_LIFETIME_DAYS` in `@cineamo/ui`, which writes the same cookie. */
const CONSENT_LIFETIME_DAYS = 180;

export type PulseConsentSyncOptions = {
    /** Defaults to {@link PULSE_CONSENT_COOKIE_NAME}. Pass the same name everywhere the cookie is read. */
    cookieName?: string;

    /** Defaults to {@link PULSE_CONSENT_CHANGED_EVENT}. Pass the same name to the withdrawal hook. */
    eventName?: string;

    /**
     * Cookie `Domain`. Omitted by default, which yields a host-only cookie. Set it to the registrable
     * domain (`.example.com`) when the subdomains that track share one consent decision — and decide
     * it once: a host-only and a domain cookie of the same name are two cookies, and a read sees
     * whichever the browser lists first.
     */
    domain?: string;

    /** Days the decision is kept. Defaults to 180, as Cineamo's own banner does. */
    lifetimeDays?: number;
};

/**
 * Mirrors a CMP's decision into the consent cookie and tells the page — the one call a partner makes.
 *
 * `true` is granted and `false` is denied; `undefined` means the visitor has not decided (or the
 * decision is gone), and deletes the cookie, which every reader resolves to `unknown`. Call it from
 * every CMP callback with the CMP's answer for Cineamo as a vendor; reading the CMP (consentmanager's
 * `__cmp('getCMPData')`, OneTrust, Usercentrics, ...) is the only part that stays the partner's.
 *
 * **Dispatches only when the cookie actually changes.** CMPs report their state on every page load,
 * not only when the visitor acts, and the change event is what makes a surface re-send the consent
 * visit's page view and what {@link usePulseConsentWithdrawal} turns into a withdrawal — so it has
 * to mean "changed". Grant and revoke both dispatch: a surface that only announced grants would
 * never send the withdrawal, and the backend would keep the identity marked granted.
 *
 * `SameSite=Lax`, and `Secure` whenever the page is served over https; both are invisible to the
 * same-site requests the route handler reads the cookie on. A no-op wherever there is no `document`.
 *
 * @returns whether the cookie changed, and so whether the event was dispatched.
 */
export function syncPulseConsent(granted: boolean | undefined, options: PulseConsentSyncOptions = {}): boolean {
    if (typeof document === 'undefined') return false;

    const cookieName = options.cookieName ?? PULSE_CONSENT_COOKIE_NAME;
    const next = granted === undefined ? undefined : String(granted);

    if (readCookie(document.cookie, cookieName) === next) return false;

    const expires =
        next === undefined
            ? new Date(0)
            : new Date(Date.now() + (options.lifetimeDays ?? CONSENT_LIFETIME_DAYS) * 24 * 60 * 60 * 1000);

    document.cookie = [
        `${cookieName}=${next ?? ''}`,
        `expires=${expires.toUTCString()}`,
        'path=/',
        ...(options.domain ? [`domain=${options.domain}`] : []),
        'SameSite=Lax',
        ...(typeof location !== 'undefined' && location.protocol === 'https:' ? ['Secure'] : [])
    ].join('; ');

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(options.eventName ?? PULSE_CONSENT_CHANGED_EVENT));
    }

    return true;
}
